import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment, { MomentInput } from 'moment';
import 'moment/locale/fr';
import 'moment/locale/de';
import { fr, en, de } from './translations';

const dotify = (obj: AnyObj) => {
    const res: AnyObj = {};
    function recurse(obj: AnyObj, current?: string) {
        for (var key in obj) {
            var value = obj[key];
            var newKey = (current ? current + '.' + key : key);  // joined key with dot
            if (value && typeof value === 'object') {
                recurse(value, newKey);  // it's a nested object, so do it again
            } else {
                res[newKey] = value;  // it's not an object, so set the property
            }
        }
    }

    recurse(obj);
    return res;
};

const resources = {
    en: { translations: dotify(en) },
    fr: { translations: dotify(fr) },
    de: { translations: dotify(de) },
};

i18n
    .use(initReactI18next)
    .use(LanguageDetector)
    .init({
        // we init with resources
        resources: resources,
        fallbackLng: 'fr',
        debug: false,

        supportedLngs: ['fr', 'en', 'de'],

        // have a common namespace used around the full app
        ns: ['translations'],
        defaultNS: 'translations',

        keySeparator: false, // we use content as keys

        interpolation: {
            escapeValue: false, // not needed for react!!
            formatSeparator: ','
        },

        detection: {
            // Notification links use ?lang=; i18next default is ?lng=
            order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
            lookupQuerystring: 'lang',
            caches: ['localStorage'],
        },

        react: {}
    });

const uiLanguage = (lang = i18n.language) => (lang || 'fr').split('-')[0] || 'fr';

/** BCP-47 tags for Intl. Prefer fr-FR so short dates use DD/MM/YYYY (not fr-CH dots). */
const intlLocale = (lang = i18n.language) => ({
    fr: 'fr-FR',
    en: 'en-GB',
    de: 'de-CH',
}[uiLanguage(lang)] || 'fr-FR');

const locale = () => ({
    fr: 'fr-CH',
    en: 'en-GB',
    de: 'de-CH',
}[uiLanguage()] || 'fr-CH');

const syncMomentLocale = (lang = i18n.language) => {
    moment.locale(uiLanguage(lang));
};

syncMomentLocale();
i18n.on('languageChanged', syncMomentLocale);

const formatPrice = (n: number) => {
    const formatter = new Intl.NumberFormat(locale(), {
        style: 'currency',
        currency: 'CHF',
    });

    return formatter.format(n);
};

const formatLocalizedDate = (date: Date, lang = i18n.language) =>
    new Intl.DateTimeFormat(intlLocale(lang), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(date);

/**
 * Format a date for the active UI language via Intl (not moment locales).
 * Moment instance locales are sticky and Vite can fail to apply `moment/locale/*`.
 */
const formatMomentDate = (value: MomentInput, format: string, lang = i18n.language) => {
    const date = moment(value).toDate();
    const tag = intlLocale(lang);

    switch (format) {
        case 'L':
            return new Intl.DateTimeFormat(tag, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).format(date);
        case 'll':
            return new Intl.DateTimeFormat(tag, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            }).format(date);
        case 'LL':
            return new Intl.DateTimeFormat(tag, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).format(date);
        case 'LT':
            return new Intl.DateTimeFormat(tag, {
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        case 'LLL':
            return new Intl.DateTimeFormat(tag, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        case 'dd DD MMM YYYY HH:mm':
            return new Intl.DateTimeFormat(tag, {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        default:
            // Fallback: still force UI language on a fresh moment instance.
            return moment(value).locale(uiLanguage(lang)).format(format);
    }
};

export default i18n;
export { formatPrice, formatLocalizedDate, formatMomentDate, syncMomentLocale, uiLanguage, intlLocale };
